import { ButtonProps } from "@mui/material";
import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { ConfirmationDialog } from "../common/components/functional/confirmationDialog/ConfirmationDialog";

export type ConfirmDialogOptions = {
  title: string;
  contentText: string | ReactElement;
  confirmText: string;
  cancelText?: string;
  CancelButtonProps?: ButtonProps;
  ConfirmButtonProps?: ButtonProps;
};

type ConfirmationDialogContextValue = {
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
};

const ConfirmationDialogContext = createContext<ConfirmationDialogContextValue>(
  {
    confirm: () => Promise.resolve(false),
  },
);

export const useConfirmationDialog = () =>
  useContext(ConfirmationDialogContext);

type DialogState = {
  open: boolean;
  options: ConfirmDialogOptions;
};

const defaultOptions: ConfirmDialogOptions = {
  title: "",
  contentText: "",
  confirmText: "",
};

export const ConfirmationDialogProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    options: defaultOptions,
  });

  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback(
    (options: ConfirmDialogOptions): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        setDialogState({ open: true, options });
      });
    },
    [],
  );

  const handleConfirm = () => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setDialogState((prev) => ({ ...prev, open: false }));
  };

  const handleCancel = () => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setDialogState((prev) => ({ ...prev, open: false }));
  };

  return (
    <ConfirmationDialogContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationDialog
        isOpen={dialogState.open}
        title={dialogState.options.title}
        contentText={dialogState.options.contentText}
        confirmText={dialogState.options.confirmText}
        cancelText={dialogState.options.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        CancelButtonProps={dialogState.options.CancelButtonProps}
        ConfirmButtonProps={dialogState.options.ConfirmButtonProps}
      />
    </ConfirmationDialogContext.Provider>
  );
};
